import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  MessageCircle, Plus, Send, Edit2, Trash2, Save, X, 
  Clock, CheckCircle, XCircle, Users, Calendar,
  Search
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  fetchMarketingMessages,
  createMessage,
  updateMessage,
  deleteMessage,
  sendMarketingMessage
} from '../../../redux/features/marketingSlice';
import { fetchClientsAsync } from '../../../redux/features/clientSlice';

const ViberView = () => {
  const dispatch = useDispatch();
  const { messages, loading } = useSelector((state) => state.marketing);
  const { clients } = useSelector((state) => state.client);
  const [showForm, setShowForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    scheduledAt: ''
  });

  useEffect(() => {
    dispatch(fetchMarketingMessages('viber'));
    dispatch(fetchClientsAsync());
  }, [dispatch]);

  const viberMessages = messages.filter(msg => msg.type === 'viber');

  const filteredMessages = viberMessages.filter(msg =>
    msg.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRecipientToggle = (clientId) => {
    setSelectedRecipients(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handleSelectAll = () => {
    const clientsWithPhone = clients.filter(c => c.phone).map(c => c._id);
    if (selectedRecipients.length === clientsWithPhone.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(clientsWithPhone);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast.error('Naslov i sadržaj su obavezni');
      return;
    }

    if (selectedRecipients.length === 0) {
      toast.error('Izaberite bar jednog primaoca');
      return;
    }

    const messageData = {
      type: 'viber',
      title: formData.title,
      subject: formData.title,
      content: formData.content,
      recipientIds: selectedRecipients,
      scheduledAt: formData.scheduledAt || null
    };

    try {
      if (editingMessage) {
        await dispatch(updateMessage({ id: editingMessage._id, messageData })).unwrap();
        setEditingMessage(null);
      } else {
        await dispatch(createMessage(messageData)).unwrap();
      }
      
      resetForm();
      setShowForm(false);
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setFormData({
      title: message.title,
      content: message.content,
      scheduledAt: message.scheduledAt ? new Date(message.scheduledAt).toISOString().slice(0, 16) : ''
    });
    setSelectedRecipients(message.recipients.map(r => r.clientId?._id || r.clientId).filter(Boolean));
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovu poruku?')) {
      try {
        await dispatch(deleteMessage(id)).unwrap();
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
  };

  const handleSend = async (id) => {
    if (window.confirm('Da li ste sigurni da želite da pošaljete ovu poruku?')) {
      try {
        await dispatch(sendMarketingMessage(id)).unwrap();
        dispatch(fetchMarketingMessages('viber'));
      } catch (error) {
        console.error('Error sending message:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      scheduledAt: ''
    });
    setSelectedRecipients([]);
    setEditingMessage(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'sending':
        return <Clock className="h-5 w-5 text-yellow-400 animate-spin" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-400" />;
      case 'scheduled':
        return <Calendar className="h-5 w-5 text-blue-400" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'sent': return 'Poslato';
      case 'sending': return 'Šalje se';
      case 'failed': return 'Neuspešno';
      case 'scheduled': return 'Zakazano';
      case 'draft': return 'Nacrt';
      default: return status;
    }
  };

  const clientsWithPhone = clients.filter(c => c.phone);

  return (
    <div className="bg-gray-900 min-h-screen p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="bg-purple-500/20 rounded-lg p-4">
                <MessageCircle className="h-8 w-8 text-purple-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Viber Marketing</h1>
                <p className="text-gray-400 mt-1">Slanje Viber poruka vašim klijentima</p>
              </div>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowForm(!showForm);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition"
            >
              <Plus className="h-5 w-5" />
              {showForm ? 'Otkaži' : 'Nova Poruka'}
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Pretraži poruke..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">
              {editingMessage ? 'Izmeni Poruku' : 'Nova Viber Poruka'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Naslov *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sadržaj *
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  rows={6}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Zakazano vreme (opciono)
                </label>
                <input
                  type="datetime-local"
                  name="scheduledAt"
                  value={formData.scheduledAt}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                />
              </div>

              {/* Recipient Selector */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Primaoci * ({selectedRecipients.length} izabrano)
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowRecipientSelector(!showRecipientSelector)}
                    className="text-purple-400 hover:text-purple-300 text-sm"
                  >
                    {showRecipientSelector ? 'Sakrij' : 'Izaberi Primaoce'}
                  </button>
                </div>
                
                {showRecipientSelector && (
                  <div className="bg-gray-700 rounded-lg p-4 max-h-60 overflow-y-auto">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-300">
                        {clientsWithPhone.length} klijenata sa telefonskim brojem
                      </span>
                      <button
                        type="button"
                        onClick={handleSelectAll}
                        className="text-sm text-purple-400 hover:text-purple-300"
                      >
                        {selectedRecipients.length === clientsWithPhone.length ? 'Poništi sve' : 'Izaberi sve'}
                      </button>
                    </div>
                    <div className="space-y-2">
                      {clientsWithPhone.map(client => (
                        <label
                          key={client._id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-600 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedRecipients.includes(client._id)}
                            onChange={() => handleRecipientToggle(client._id)}
                            className="rounded"
                          />
                          <span className="text-white text-sm">
                            {client.name} ({client.phone})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition"
                >
                  <Save className="h-5 w-5" />
                  {editingMessage ? 'Sačuvaj Izmene' : 'Sačuvaj'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                  className="flex items-center gap-2 px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition"
                >
                  <X className="h-5 w-5" />
                  Otkaži
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Messages List */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">Sačuvane Poruke</h2>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-400 mx-auto"></div>
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              Nema sačuvanih poruka
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMessages.map((message) => (
                <div
                  key={message._id}
                  className="bg-gray-700 rounded-lg p-4 border border-gray-600 hover:border-purple-500 transition"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{message.title}</h3>
                        {getStatusIcon(message.status)}
                        <span className="text-sm text-gray-400">{getStatusText(message.status)}</span>
                      </div>
                      <p className="text-gray-300 mb-2">{message.content}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {message.totalRecipients} primaoca
                        </div>
                        {message.sentAt && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            Poslato: {new Date(message.sentAt).toLocaleString('sr-RS')}
                          </div>
                        )}
                        {message.scheduledAt && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Zakazano: {new Date(message.scheduledAt).toLocaleString('sr-RS')}
                          </div>
                        )}
                        {message.status === 'sent' && (
                          <>
                            <span className="text-green-400">
                              ✓ {message.sentCount} poslato
                            </span>
                            {message.failedCount > 0 && (
                              <span className="text-red-400">
                                ✗ {message.failedCount} neuspešno
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {message.status === 'draft' && (
                        <>
                          <button
                            onClick={() => handleEdit(message)}
                            className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition"
                            title="Izmeni"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleSend(message._id)}
                            className="p-2 bg-green-600 hover:bg-green-700 text-white rounded transition"
                            title="Pošalji"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {message.status === 'scheduled' && (
                        <button
                          onClick={() => handleSend(message._id)}
                          className="p-2 bg-green-600 hover:bg-green-700 text-white rounded transition"
                          title="Pošalji sada"
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(message._id)}
                        className="p-2 bg-red-600 hover:bg-red-700 text-white rounded transition"
                        title="Obriši"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViberView;
